import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileDown } from 'lucide-react';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { Badge } from '@/components/Shared/Badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/Shared/Tabs';
import EmptyState from '@/components/Shared/EmptyState';
import SearchSelect from '@/components/Shared/SearchSelect';
import { ProtocolEmployeeCard } from './ProtocolEmployeeCard';
import { ProtocolReportsCard } from './ProtocolReportsCard';
import { useChecklistProtocolStore } from '@/store/useChecklistProtocolStore';
import { useOpenItemsStore } from '@/store/useOpenItemsStore';
import { formatDate, parseIsoDate } from '@/utils/dateUtils';
import { useEmployeeStore } from '@/store/useEmployeeStore';
import { useObjectStore } from '@/store/useObjectStore';
import { useToastStore } from '@/store/useToastStore';
import { downloadChecklistProtocolPdf } from '@/utils/downloadReport';

function todayString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ChecklistProtocolPage() {
  const { t } = useTranslation('checklistProtocol');
  const { protocol, loading, error, fetchProtocol, openPhoto } = useChecklistProtocolStore();
  const openProtocol = useChecklistProtocolStore((s) => s.openProtocol);
  const openLoading = useChecklistProtocolStore((s) => s.openLoading);
  const openError = useChecklistProtocolStore((s) => s.openError);
  const fetchOpenProtocol = useChecklistProtocolStore((s) => s.fetchOpenProtocol);
  const { employees, fetchEmployees } = useEmployeeStore();
  const { objects, fetchAllObjects } = useObjectStore();
  const { addToast } = useToastStore();
  const fetchOpenItems = useOpenItemsStore((s) => s.fetchOpenItems);

  const [view, setView] = useState<'day' | 'open'>('day');
  const [date, setDate] = useState(todayString());
  const [employeeId, setEmployeeId] = useState('');
  const [objectId, setObjectId] = useState('');
  // Off by default: the open view then lists only unfinished work, as before.
  const [showCompleted, setShowCompleted] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchAllObjects();
  }, [fetchEmployees, fetchAllObjects]);

  useEffect(() => {
    if (view === 'day') fetchProtocol({ date, employeeId, objectId });
  }, [view, date, employeeId, objectId, fetchProtocol]);

  useEffect(() => {
    if (view === 'open') fetchOpenProtocol({ employeeId, objectId, includeCompleted: showCompleted });
  }, [view, employeeId, objectId, showCompleted, fetchOpenProtocol]);

  const employeeOptions = useMemo(
    () =>
      employees
        .map((e) => ({ id: e.id ?? '', label: `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim() }))
        .filter((o) => o.id),
    [employees],
  );

  const objectOptions = useMemo(
    () => objects.map((o) => ({ id: o.id ?? '', label: o.name ?? '' })).filter((o) => o.id),
    [objects],
  );

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadChecklistProtocolPdf(date, employeeId || undefined, objectId || undefined);
    } catch {
      addToast(t('errors.downloadFailed'), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const viewPhoto = async (documentId: string) => {
    try {
      await openPhoto(documentId);
    } catch {
      addToast(t('errors.photoFailed'), 'error');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card — same shell as every other admin page. */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {view === 'open'
                ? showCompleted
                  ? t('open.subtitleWithCompleted')
                  : t('open.subtitle')
                : t('subtitle')}
            </p>
          </div>
          {/* No PDF for the open view — the report is defined per day. */}
          {view === 'day' && (
            <Button
              variant="secondary"
              onClick={handleDownload}
              disabled={downloading || loading}
              leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('actions.downloadPdf')}
            </Button>
          )}
        </div>

        <Tabs value={view} onValueChange={(v) => setView(v as 'day' | 'open')} className="mt-3">
          <TabsList>
            <TabsTrigger value="day">{t('views.day')}</TabsTrigger>
            <TabsTrigger value="open">{t('views.open')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card>
        <div
          className={`grid grid-cols-1 gap-3 ${view === 'open' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}
        >
          {view === 'day' && (
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('filters.date')}
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
              />
            </div>
          )}
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
              {t('filters.employee')}
            </label>
            <SearchSelect
              options={employeeOptions}
              value={employeeId}
              onChange={setEmployeeId}
              placeholder={t('filters.allEmployees')}
              noResultsText={t('filters.noResults')}
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
              {t('filters.object')}
            </label>
            <SearchSelect
              options={objectOptions}
              value={objectId}
              onChange={setObjectId}
              placeholder={t('filters.allObjects')}
              noResultsText={t('filters.noResults')}
            />
          </div>
        </div>
        {view === 'open' && (
          <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-600">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
            />
            {t('open.showCompleted')}
          </label>
        )}
      </Card>

      {view === 'day' ? (
        <>
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#00A871]" />
            </div>
          )}

          {error && !loading && (
            <EmptyState
              variant="error"
              title={t('errors.fetchFailed')}
              onRetry={() => fetchProtocol({ date, employeeId, objectId })}
            />
          )}

          {!loading && !error && protocol && (
            <>
              <div className="flex items-center gap-3 text-[13px] text-slate-600">
                <Badge variant="success">
                  {t('summary.completed', { count: protocol.completedCount ?? 0 })}
                </Badge>
                <Badge variant={(protocol.openCount ?? 0) > 0 ? 'warning' : 'default'}>
                  {t('summary.open', { count: protocol.openCount ?? 0 })}
                </Badge>
              </div>

              {(protocol.reports ?? []).length > 0 && (
                <ProtocolReportsCard
                  reports={protocol.reports ?? []}
                  onOpenReports={() => void fetchOpenItems('reports')}
                  t={t}
                />
              )}

              {(protocol.employees ?? []).length === 0 ? (
                <EmptyState title={t('empty')} />
              ) : (
                <div className="space-y-4">
                  {(protocol.employees ?? []).map((employee) => (
                    <ProtocolEmployeeCard
                      key={employee.employeeId}
                      employee={employee}
                      onViewPhoto={viewPhoto}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {openLoading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#00A871]" />
            </div>
          )}

          {openError && !openLoading && (
            <EmptyState
              variant="error"
              title={t('open.errors.fetchFailed')}
              onRetry={() =>
                fetchOpenProtocol({ employeeId, objectId, includeCompleted: showCompleted })
              }
            />
          )}

          {!openLoading && !openError && openProtocol && (
            <>
              <div className="flex items-center gap-3 text-[13px] text-slate-600">
                <Badge variant={(openProtocol.openCount ?? 0) > 0 ? 'warning' : 'default'}>
                  {t('summary.open', { count: openProtocol.openCount ?? 0 })}
                </Badge>
              </div>

              {(openProtocol.days ?? []).length === 0 ? (
                <EmptyState title={showCompleted ? t('open.emptyWithCompleted') : t('open.empty')} />
              ) : (
                <div className="space-y-6">
                  {(openProtocol.days ?? []).map((day) => (
                    <div key={day.date} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                          {day.date ? formatDate(parseIsoDate(day.date)) : '–'}
                        </span>
                        <Badge variant={(day.openCount ?? 0) > 0 ? 'warning' : 'default'}>
                          {t('summary.open', { count: day.openCount ?? 0 })}
                        </Badge>
                      </div>
                      <div className="space-y-4">
                        {(day.employees ?? []).map((employee) => (
                          <ProtocolEmployeeCard
                            key={`${day.date}-${employee.employeeId}`}
                            employee={employee}
                            onViewPhoto={viewPhoto}
                            t={t}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
